import { Button } from "@mui/material";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState, useCallback } from "react";

import DataContext from "../../../../../../../../../context/DataContext";
import axios from "../../../../../../../../api/axios";
import LoadingIndicator from "../../../../../../../../global/LoadingIndicator";
import ResponseModal from "../../../../../../../../global/ResponseModal";
import CaseSummaryContext from "../../../../CaseSummaryV2/context/CaseSummaryContext";
import { ClientDetailContext } from "../../../../context/ClientDetailContext";
import AssociateAppointmentsTable from "../AssociateAppointmentsTable";

function AssociateAppointments({
  document,
  clientId,
  setOpenAssociateAppointmentsModal,
  docTrigger,
  setDocTrigger,
  closeUploadModal,
}) {
  const { userRoles } = useContext(DataContext);
  const { documentTrigger, setDocumentTrigger } = useContext(CaseSummaryContext);
  const {
    selectedAppointment,
    isLaunchedFromAppointmentsTab,
    setIsLaunchedFromAppointmentsTab,
    reloadClientDetail,
    setReloadClientDetail,
  } = useContext(ClientDetailContext);

  const [appointmentList, setAppointmentList] = useState([]);
  const [next, setNext] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const buttonStyle = { width: "120px", height: "40px" };
  const provider = document && document.provider ? document.provider : "";

  const canAddAssociatedDocuments = !!userRoles?.permissions?.includes(
    "filemanager.add_document_association"
  );

  const handleSaveSuccess = () => {
    setReloadClientDetail(!reloadClientDetail);
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal();
      setDocumentTrigger(!documentTrigger);
      setDocTrigger(!docTrigger);
      setIsLaunchedFromAppointmentsTab(false);
    } else {
      setDocTrigger(!docTrigger);
      setOpenAssociateAppointmentsModal(false);
    }
  };

  const handleCancel = () => {
    if (isLaunchedFromAppointmentsTab) {
      closeUploadModal();
      setIsLaunchedFromAppointmentsTab(false);
    } else {
      setOpenAssociateAppointmentsModal(false);
    }
  };

  const handleSaveFail = () => {
    setOpenAssociateAppointmentsModal(false);
  };

  const buildLocalDateTime = (dateStr, timeStr = "00:00:00") => {
    if (!dateStr) {
      return null;
    }
    const [y, m, d] = String(dateStr).split("-").map(Number);
    const [hh, mm, ss] = String(timeStr)
      .split(":")
      .map((n) => Number(n || 0));
    if (
      !Number.isInteger(y) ||
      !Number.isInteger(m) ||
      !Number.isInteger(d) ||
      !Number.isInteger(hh) ||
      !Number.isInteger(mm) ||
      !Number.isInteger(ss)
    ) {
      return null;
    }
    return new Date(y, m - 1, d, hh, mm, ss, 0);
  };

  const covertDateFollowFormat = (dateStr, timeStr) => {
    const dateObj = buildLocalDateTime(dateStr, timeStr);
    if (!dateObj) {
      return { date: "", time: "" };
    }

    const date = `${(dateObj.getMonth() + 1).toString().padStart(2, "0")}/${dateObj
      .getDate()
      .toString()
      .padStart(2, "0")}/${dateObj.getFullYear()}`;

    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const time = `${hours % 12 || 12}:${minutes} ${ampm}`;

    return { date, time };
  };

  const formatAppointments = useCallback((list) => {
    return list.map((app) => {
      const totalAmount = (app.administered_services || []).reduce((sum, service) => {
        const price = Number(service?.price ?? 0);
        return sum + (isNaN(price) ? 0 : price);
      }, 0);
      const amount = `$${totalAmount.toFixed(2)}`;

      const { date, time } = covertDateFollowFormat(
        app.appointment_date, // "YYYY-MM-DD"
        app.appointment_time // "HH:mm:ss"
      );

      return {
        id: app.pk,
        date,
        time,
        amount,
      };
    });
  }, []);

  const handleSaveData = () => {
    setIsLoading(true);
    const idsAssociatedAppointments = appointmentList
      .filter((item) => item.included)
      .map((item) => item.id);

    const documentId = document?.pk ? document?.pk : document?.id;

    axios
      .patch(
        `/api/documents/${documentId}/`,
        { appointments: idsAssociatedAppointments },
        {
          headers: {
                        "Content-Type": "application/json",
          },
        }
      )
      .then(() => setSaveSuccess(true))
      .catch(() => setSaveFailed(true))
      .finally(() => setIsLoading(false));
  };

  const fetchAppointments = useCallback(
    async (url) => {
      setIsLoading(true);
      axios
        .get(url, {
          headers: {
                        "Content-Type": "application/json",
          },
        })
        .then((response) => {
          const allAppointments = formatAppointments(response.data.results || []);
          const selAppointmentPk = selectedAppointment?.pk;
          const selAppointment = selAppointmentPk
            ? allAppointments.filter((appt) => appt.id === selAppointmentPk)
            : [];

          const responseAppointments = isLaunchedFromAppointmentsTab
            ? selAppointment
            : allAppointments;

          setAppointmentList((prev) => [...prev, ...responseAppointments]);
          setNext(response.data.next || null);
        })
        .catch(() => {
          setSaveFailed(true);
        })
        .finally(() => setIsLoading(false));
    },
    [formatAppointments, isLaunchedFromAppointmentsTab, selectedAppointment]
  );

  const handleScroll = () => {
    if (next && !isLoading) {
      fetchAppointments(next);
    }
  };

  useEffect(() => {
    fetchAppointments(
      `/api/appointments/?limit=100&provider=${provider}&client=${clientId}&ordering=-appointment_date,-appointment_time&include_administered=true`
    );
  }, [document, clientId, provider, fetchAppointments]);

  return (
    <>
      {isLoading ? (
        <div className="min-h-[65vh] w-full">
          <LoadingIndicator isLoading={isLoading} />
        </div>
      ) : (
        <>
          <div className="flex w-[100%]">
            {!isLaunchedFromAppointmentsTab && (
              <div className="w-[60%]">
                <div>
                  {document && document.file ? (
                    <object
                      name={document.file_name}
                      data={document.file}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        minHeight: "70vh",
                      }}
                    />
                  ) : (
                    <p
                      style={{
                        fontSize: "1.2rem",
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      No File Preview Available
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="grow">
              <AssociateAppointmentsTable
                next={next}
                document={document}
                includedAppointments={
                  isLaunchedFromAppointmentsTab ? document : document?.appointments
                }
                appointmentList={appointmentList}
                setAppointmentList={setAppointmentList}
                handleScroll={handleScroll}
                canAddAssociatedDocuments={canAddAssociatedDocuments}
              />
            </div>
          </div>

          <div>
            {canAddAssociatedDocuments && (
              <div className="space-between mt-6 flex justify-end gap-4">
                <Button
                  style={buttonStyle}
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!appointmentList || appointmentList.length === 0}
                  style={buttonStyle}
                  variant="contained"
                  color="primary"
                  onClick={handleSaveData}
                >
                  Save
                </Button>
              </div>
            )}
          </div>

          <ResponseModal
            title="Appointments successfully associated and saved"
            description="You've updated the Association of Appointments to Documents successfully"
            openBool={saveSuccess}
            setOpenBool={setSaveSuccess}
            handleCloseFunc={handleSaveSuccess}
          />
          <ResponseModal
            title="Can't save your associated appointments"
            isError={true}
            openBool={saveFailed}
            setOpenBool={setSaveFailed}
            errorMessage={null}
            handleCloseFunc={handleSaveFail}
          />
        </>
      )}
    </>
  );
}

AssociateAppointments.propTypes = {
  document: PropTypes.shape({
    provider: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  clientId: PropTypes.number,
  setOpenAssociateAppointmentsModal: PropTypes.func,
  docTrigger: PropTypes.bool,
  setDocTrigger: PropTypes.func,
  closeUploadModal: PropTypes.func,
};

export default AssociateAppointments;
